import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  BookOpen,
  FilePenLine,
  Receipt,
  ShieldCheck,
} from "lucide-react";
import type { ReactNode } from "react";
import { PERMISSIONS, userHasPermission } from "@/auth/permissions";
import { navigationConfig } from "@/config/navigation";
import { useUser } from "@/hooks/useAuth";
import { useFilteredNavItems } from "@/hooks/useSidebar";

export function StudioDashboard({ summary }: { summary: ReactNode }) {
  const user = useUser();
  const entries = useFilteredNavItems(navigationConfig.main);
  const links = entries
    .flatMap((entry) => (entry.kind === "link" ? [entry] : entry.children))
    .filter((link) => link.section);
  const actions = [
    {
      title: "Generator wypowiedzeń",
      description: "Klient, dane polisy i dokument do podpisu.",
      icon: FilePenLine,
      to: "/handbook",
      permission: PERMISSIONS.TOOLKIT.READ,
      tool: "termination" as const,
    },
    {
      title: "Zgody RODO",
      description: "Klienci, potwierdzenia i zgody papierowe.",
      icon: ShieldCheck,
      to: "/clients",
      permission: PERMISSIONS.CLIENTS.READ,
    },
    {
      title: "Rozliczenia",
      description: "Prowizje, dokumenty i statusy wypłat.",
      icon: Receipt,
      to: "/settlements",
      permission: PERMISSIONS.SETTLEMENTS.READ,
    },
    {
      title: "Podręcznik Agenta",
      description: "Znajdź odpowiedź, dokument lub kontakt.",
      icon: BookOpen,
      to: "/handbook",
      permission: PERMISSIONS.TOOLKIT.READ,
    },
  ].filter((action) => userHasPermission(user, action.permission));
  return (
    <div className="studio-dashboard">
      <div className="studio-home-heading">
        <div>
          <p className="studio-eyebrow">Dzień dobry, {user?.firstName}</p>
          <h1>Od czego zaczynamy?</h1>
          <p className="studio-description">
            Twoi klienci, dokumenty i sprawy w jednym miejscu.
          </p>
        </div>
        <span className="studio-date">
          {new Intl.DateTimeFormat("pl-PL", {
            dateStyle: "long",
            timeZone: "Europe/Warsaw",
          }).format(new Date())}
        </span>
      </div>
      <div className="studio-quick-actions">
        {actions.map((action) => (
          <Link
            key={action.title}
            to={action.to}
            search={action.tool ? { tool: action.tool } : {}}
            className="studio-action"
          >
            <span className="studio-action-icon">
              <action.icon size={21} aria-hidden />
            </span>
            <h2>{action.title}</h2>
            <p>{action.description}</p>
            <ArrowRight size={17} aria-hidden className="studio-action-arrow" />
          </Link>
        ))}
      </div>
      <section className="studio-summary" aria-label="Podsumowanie konta">
        {summary}
      </section>
      {links.length > 0 && (
        <section>
          <div className="studio-section-heading">
            <h2>Twoja przestrzeń pracy</h2>
            <span>Dostępna według uprawnień</span>
          </div>
          <div className="studio-catalog">
            {links.map((link) => (
              <Link to={link.href} key={link.id}>
                <span className="studio-catalog-icon">{link.icon}</span>
                <span>
                  <strong>{link.label}</strong>
                  <small>{link.section}</small>
                </span>
                <ArrowRight size={16} aria-hidden />
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
